import React, { FC, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from '../../../../common/hooks/helper/useTranslation'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Collapse,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
} from '@mui/icons-material'

import { useMutation, useQuery } from '@apollo/client'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useUserProfile } from '../../../../common/hooks/useUserProfile'

import {
  FIND_PATIENT_PROFILE,
  INVITE_PATIENT,
  INVITE_EXISTING_PATIENT,
} from '../../../../operations/doctorPatientOperations'
import {
  inviteExistingPatient,
  inviteExistingPatientVariables,
  invitePatient,
  invitePatientVariables,
  findPatientProfile,
  findPatientProfileVariables,
  UserType,
} from '../../../../models/graphqlTypes'
import { Loading } from '../../../../common/components/Loading'
import { useStoreActions } from '../../../../store/store.hooks'
import { useUserType } from '../../../../common/hooks/useUserType'
import { DoctorSelector } from '../../../../common/components/selectors/DoctorSelector'
import { PatientListItemType } from '../types/patientList.types'
import { parseTajNumber } from '../../../../utils/taj'
import {
  AddPatientForm,
  NewPatientForm,
} from './AddPatientModal/AddPatientForm'
import { CloseIcon } from '../../../../common/icons/CloseIcon'
import { hasContact } from '../../../../utils/hasContact'

type Props = {
  patientList?: PatientListItemType[]
  isOpen: boolean
  toggleIsOpen: (isOpen: boolean) => void
}

const defaultValues: NewPatientForm = {
  tajNumber: '',
  birthDate: null,
  birthPlace: '',
  gender: '',
  title: '',
  firstName: '',
  lastName: '',
  mothersLastName: '',
  mothersFirstName: '',
  height: '',
  weight: '',
  email: '',
  phone: '',
  emergencyContactFirstName: '',
  emergencyContactLastName: '',
  emergencyContactPhone: '',
  infoRecipientFirstName: '',
  infoRecipientLastName: '',
  infoRecipientPhone: '',
  patientSigned: false,
  assistantOwnDoctors: [],
}

const AddPatientModal: FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  toggleIsOpen,
  patientList = [],
}) => {
  const { t } = useTranslation()
  const history = useHistory()

  const setToast = useStoreActions((actions) => actions.toast.setToast)

  const [invalidEmailValues, setInvalidEmailValues] = useState<string[]>([])
  const [showError, setShowError] = useState<boolean>(false)

  const methods = useForm<NewPatientForm>({
    mode: 'onChange',
    defaultValues,
  })

  const { watch, handleSubmit, control, trigger, reset } = methods

  const tajNumber = isOpen ? watch('tajNumber') : ''
  const tajDigitCount = (tajNumber.match(/\d+/g) || []).join('').length
  const validTajNumber = parseTajNumber(tajNumber)
  const isTajValid = !!validTajNumber
  const userType = useUserType()

  const urlUserTypeSegment = useMemo(
    () => (userType === UserType.Assistant ? 'assistant' : 'doctor'),
    [userType]
  )

  const { data: storedProfileData, loading: searchingProfile } = useQuery<
    findPatientProfile,
    findPatientProfileVariables
  >(FIND_PATIENT_PROFILE, {
    skip: !isTajValid,
    variables: {
      tajNumber: validTajNumber || '',
    },
  })

  // Check storeProfileData in useEffect instead of the query onCompleted fn
  // as it's not complete properly after modal is reopened
  useEffect(() => {
    if (
      !storedProfileData ||
      storedProfileData.findPatientProfile.__typename ===
        'PatientProfileNotFoundError'
    ) {
      return
    }

    reset({
      tajNumber: storedProfileData.findPatientProfile.tajNumber,
      birthDate: storedProfileData.findPatientProfile.birthDate,
      title: storedProfileData.findPatientProfile.title,
      firstName: storedProfileData.findPatientProfile.firstName,
      lastName: storedProfileData.findPatientProfile.lastName,
      gender: storedProfileData.findPatientProfile.gender,
      email: ' ', // it should be a not null string
      phone: storedProfileData.findPatientProfile.phone,
      patientSigned: true,
      assistantOwnDoctors: [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedProfileData])

  const profileToAdd =
    storedProfileData?.findPatientProfile.__typename === 'PatientProfile'
      ? storedProfileData?.findPatientProfile
      : undefined

  const doctorOwnProfile = patientList.find(
    (patient) => patient && patient.id === profileToAdd?.id
  )
  const isInvalidTaj = tajNumber.length >= 9 && !isTajValid

  const patientHasProfile = !searchingProfile && isTajValid && !!profileToAdd
  const isNewProfile = !searchingProfile && isTajValid && !profileToAdd

  const handleClose = () => {
    reset(defaultValues)
    toggleIsOpen(false)
  }

  const profile = useUserProfile()

  const [sendExistinginvite, { loading: isExistingSaving }] = useMutation<
    inviteExistingPatient,
    inviteExistingPatientVariables
  >(INVITE_EXISTING_PATIENT, {
    onCompleted: (data) => {
      setToast({
        text: t('patients:addPatientSuccess'),
        type: 'success',
      })
      const patientId = data.inviteExistingPatient[0]?.patient?.id
      if (patientId) {
        history.push(`/${urlUserTypeSegment}/patients/${patientId}`)
      }
    },
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sendInvite, { loading: isSaving }] = useMutation<
    invitePatient,
    invitePatientVariables
  >(INVITE_PATIENT, {
    onCompleted: (data) => {
      setToast({
        text: t('patients:invitePatientSuccess'),
        type: 'success',
      })
      const patientId = data.invitePatient.id
      if (patientId) {
        history.push(`/${urlUserTypeSegment}/patients/${patientId}`)
      }
    },
    onError: (error) => {
      const errorMessages: string[] = []
      const graphQlError = error.graphQLErrors[0]
      if (graphQlError) {
        const detail =
          (graphQlError.extensions?.exception?.meta as { target: string })
            ?.target || ''
        const isFieldDuplicate = !!~detail.indexOf('email')
        if (isFieldDuplicate) {
          setInvalidEmailValues([...invalidEmailValues, watch('email')])
          trigger('email')
          errorMessages.push(t('common:invite.valueExists', { field: 'email' }))
        }
      }
      setToast({
        text: errorMessages.join(', ') || t('common:serverError'),
        type: 'error',
      })
    },
  })

  const onSubmit = handleSubmit(async (createPatientArgs, event) => {
    event?.stopPropagation()
    const activePatientLink = patientList.find(
      (patient) =>
        patient.isActive && patient?.tajNumber === createPatientArgs.tajNumber
    )

    if (activePatientLink) {
      history.push(`/${urlUserTypeSegment}/patients/${activePatientLink.id}`)
      return
    }

    setShowError(false)
    delete createPatientArgs.patientSigned
    const doctorUserIds = (createPatientArgs.assistantOwnDoctors ?? [])
      .slice()
      .map((doctor) => doctor.userId)

    if (!patientHasProfile) {
      delete createPatientArgs.assistantOwnDoctors
      const emergencyContact = {
        firstName: createPatientArgs.emergencyContactFirstName,
        lastName: createPatientArgs.emergencyContactLastName,
        phone: createPatientArgs.emergencyContactPhone,
      }
      const entitleToInfoContact = {
        firstName: createPatientArgs.infoRecipientFirstName,
        lastName: createPatientArgs.infoRecipientLastName,
        phone: createPatientArgs.infoRecipientPhone,
      }
      return await sendInvite({
        variables: {
          createPatientArgs: {
            birthDate: createPatientArgs.birthDate,
            birthPlace: createPatientArgs.birthPlace,
            email: createPatientArgs.email,
            firstName: createPatientArgs.firstName,
            lastName: createPatientArgs.lastName,
            gender: createPatientArgs.gender,
            height: Number(createPatientArgs.height),
            motherFirstName: createPatientArgs.mothersFirstName,
            motherLastName: createPatientArgs.mothersLastName,
            phone: createPatientArgs.phone,
            tajNumber: createPatientArgs.tajNumber,
            title: createPatientArgs.title ?? '',
            weight: Number(createPatientArgs.weight),
            emergencyContacts: hasContact(emergencyContact)
              ? [emergencyContact]
              : [],
            entitleToInfoContacts: hasContact(entitleToInfoContact)
              ? [entitleToInfoContact]
              : undefined,
          },
          doctorUserIds,
        },
      })
    }

    if (!profileToAdd) {
      return
    }

    return await sendExistinginvite({
      variables: {
        patientId: profileToAdd.id,
        doctorUserIds,
      },
    })
  })

  const required = {
    value: true,
    message: t('messages:warnings.required'),
  }

  const handleTajClear = () => {
    reset()
  }

  const DoctorSelectorComponent = () =>
    userType === UserType.Assistant ? (
      <Box my={1}>
        <DoctorSelector
          label={t('patients:addToDoctors')}
          name="assistantOwnDoctors"
          doctorListFilterData={{ assistantId: profile?.id ?? '' }}
          required
        />
      </Box>
    ) : null

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose()
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <DialogTitle>{t('patients:inviteToBetmen')}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={() => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'common.black',
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent sx={{ pt: 0 }}>
            <Typography variant="body2">
              {t('patients:addDoctorPatient')}
            </Typography>

            <Box my={1}>
              <Typography variant="subtitle2">{t('patients:taj')}</Typography>
              <Controller
                control={control}
                name={'tajNumber'}
                rules={{ required }}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    onChange={(event) => {
                      field.onChange(event)
                      setShowError(true)
                    }}
                    value={field.value}
                    onBlur={field.onBlur}
                    autoFocus
                    autoComplete="off"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: searchingProfile ? (
                        <InputAdornment position="end">
                          <Loading size={14} />
                        </InputAdornment>
                      ) : isTajValid ? (
                        <InputAdornment position="end">
                          <CheckIcon color="inherit" fontSize="inherit" />
                        </InputAdornment>
                      ) : isInvalidTaj ? (
                        <InputAdornment position="end" onClick={handleTajClear}>
                          <ClearIcon color="inherit" fontSize="inherit" />
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                )}
              />
            </Box>

            <Collapse in={isInvalidTaj || isTajValid}>
              {showError && (
                <Box ml={0.5}>
                  {isTajValid && (
                    <Typography variant="subtitle2" color="primary">
                      {t('patients:validTaj', { tajNumber: validTajNumber })}
                    </Typography>
                  )}
                  {!isTajValid && (
                    <Typography variant="subtitle2" color="error">
                      {tajDigitCount === 9 && t('patients:invalidTaj')}
                      {tajDigitCount !== 9 && t('patients:invalidTajlength')}
                    </Typography>
                  )}
                </Box>
              )}
            </Collapse>

            <Collapse in={patientHasProfile}>
              <Box mt={2} ml={0.5}>
                <Typography variant="body1" color="textSecondary">
                  {t('patients:betmenProfileFound')}
                </Typography>

                <Box mt={1} display="flex" alignItems="center">
                  <Box px={1} fontSize={32} display="flex">
                    <PersonIcon fontSize="inherit" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">
                      {t('common:formattedNameFull', profileToAdd)}
                    </Typography>
                    <Typography variant="body2">
                      {t('common:dateFormatted', {
                        date: profileToAdd?.birthDate,
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <DoctorSelectorComponent />
            </Collapse>

            <Collapse in={isSaving || isExistingSaving}>
              <Box my={5}>
                <Loading />
              </Box>
            </Collapse>
            <Collapse in={isNewProfile && !isSaving} unmountOnExit>
              <AddPatientForm
                patientHasProfile={patientHasProfile}
                invalidEmailValues={invalidEmailValues}
              />
            </Collapse>
          </DialogContent>

          <DialogActions>
            <Button
              disabled={isSaving}
              variant="outlined"
              onClick={handleClose}
            >
              {t('common:cancel')}
            </Button>

            <Button color="primary" variant="contained" type="submit">
              {!!doctorOwnProfile
                ? t('common:choose')
                : isNewProfile
                  ? t('patients:invitePatientButton')
                  : t('patients:addPatient')}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export { AddPatientModal }
